import React, { useState, useEffect } from "react";
import { Grid, Typography, Box, useTheme } from "@mui/material";
import ApplicationCard from "./Components/Applications/ApplicationCard";
import ApplicationFormModal from "./Components/Applications/ApplicationFormModal";
import SimplePageHeader from "./Components/Navigation/SimplePageHeader"; // Assuming this component exists
import { ref, onValue, remove, update, get } from "firebase/database";
import { db } from "./firebase"; // Adjust path based on your Firebase setup
import Notification from "./Components/Notification";

function ApplicationManagementPage() {
  const [applications, setApplications] = useState({});
  const [editApp, setEditApp] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState("");

  const handleSearchChange = (term) => {
    setSearchTerm(term.toLowerCase()); // Convert to lowercase for case-insensitive comparison
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = () => {
    const appsRef = ref(db, "Applications");
    onValue(appsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setApplications(data);
      }
    });
  };

  const handleOpenForm = (appKey = null) => {
    setEditApp(appKey);
    setFormOpen(true);
  };

  const handleDelete = async (appKey) => {
    const result = await Notification.confirm({
      theme,
      title: "Delete Application",
      text: `Are you sure you want to delete the application "${applications[appKey].name}"? This action is irreversible and will remove the application from all company configurations.`,
      icon: "warning",
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      showCancelButton: true,
    });

    if (result.isConfirmed) {
      try {
        const appRef = ref(db, `Applications/${appKey}`);
        await remove(appRef);

        const companyAppsRef = ref(db, "CompanyApplications");
        const companyAppsSnapshot = await get(companyAppsRef);

        if (companyAppsSnapshot.exists()) {
          const updates = {};
          companyAppsSnapshot.forEach((companySnap) => {
            const companyCode = companySnap.key;
            const companyData = companySnap.val();

            if (companyData[appKey] !== undefined) {
              updates[`CompanyApplications/${companyCode}/${appKey}`] = null;
            }
          });

          if (Object.keys(updates).length > 0) {
            await update(ref(db), updates);
          }
        }

        Notification.success(
          theme,
          "Success",
          "Application and its references deleted successfully.",
        );
        fetchApplications();
      } catch (error) {
        console.error("Error deleting application:", error);
        Notification.error(
          theme,
          "Error",
          `There was a problem deleting the application data. Error: ${error.message}`,
        );
      }
    }
  };

  // Filter applications based on search term
  const filteredApplications = Object.keys(applications).filter(
    (key) =>
      applications[key].name.toLowerCase().includes(searchTerm) || // Filter by application name
      (applications[key].description &&
        applications[key].description.toLowerCase().includes(searchTerm)), // Optional: filter by description if exists
  );

  // Split filtered applications into default and other categories
  const defaultApps = filteredApplications.filter(
    (key) => applications[key].defaultApp,
  );
  const otherApps = filteredApplications.filter(
    (key) => !applications[key].defaultApp,
  );

  const renderApplicationCards = (appKeys) => (
    <Grid container spacing={3}>
      {appKeys.map((appKey) => (
        <Grid item xs={4} sm={3} md={2} lg={1.5} xl={1.3} key={appKey}>
          <ApplicationCard
            app={applications[appKey]}
            onEdit={() => handleOpenForm(appKey)}
            onDelete={() => handleDelete(appKey)}
          />
        </Grid>
      ))}
    </Grid>
  );

  return (
    <>
      <SimplePageHeader
        iconName="apps"
        titleString="Applications"
        placeholderText="Search by application name"
        showAddButton={true}
        onSearchChange={handleSearchChange} // Pass the handleSearchChange to update search term
        searchTerm={searchTerm}
        pageType="applications"
      />
      <Box sx={{ padding: theme.spacing(3) }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography
            color={theme.palette.success.text}
            variant="h5"
            component="div"
          >
            Default Applications
          </Typography>
        </Box>
        {renderApplicationCards(defaultApps)}

        <Box sx={{ mt: 4 }}>
          <Typography
            color={theme.palette.success.text}
            variant="h5"
            component="div"
            sx={{ mb: 2 }}
          >
            Other Available Applications
          </Typography>
          {renderApplicationCards(otherApps)}
        </Box>
      </Box>
      <ApplicationFormModal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        appKey={editApp}
        application={editApp ? applications[editApp] : null}
        fetchApplications={fetchApplications}
      />
    </>
  );
}

export default ApplicationManagementPage;
